import { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { usePortfolioSummary, useRankedSites, useModelInfo, useScoreSitesBatch } from '@/hooks';
import { ThreatTierBadge } from '@/components/ThreatTierBadge';
import { formatPercent, formatCurrency, formatDate } from '@/utils/format';
import type { ThreatTier } from '@/types';

export function RiskAnalysis() {
  const [selectedTier, setSelectedTier] = useState<number | undefined>(undefined);
  const { data: summary } = usePortfolioSummary();
  const { data: rankedSites, isLoading: sitesLoading, refetch } = useRankedSites(
    50,
    selectedTier,
    selectedTier
  );
  const { data: modelInfo } = useModelInfo();
  const batchScoreMutation = useScoreSitesBatch();

  const handleRefreshAll = async () => {
    if (rankedSites && rankedSites.length > 0) {
      const siteIds = rankedSites.map((s) => s.id);
      await batchScoreMutation.mutateAsync(siteIds);
      refetch();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-mono-800 pb-4">
        <h1 className="text-lg font-medium text-mono-100 tracking-widest">RISK ANALYSIS</h1>
        <button
          onClick={handleRefreshAll}
          disabled={batchScoreMutation.isPending || !rankedSites?.length}
          className="flex items-center gap-2 px-3 py-1.5 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 hover:text-mono-100 disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon className="h-3 w-3" />
          {batchScoreMutation.isPending ? 'SCORING...' : 'REFRESH'}
        </button>
      </div>

      {/* Model info */}
      {modelInfo && (
        <div className="border border-mono-700 p-4">
          <p className="text-[10px] text-mono-500 tracking-widest">MODEL</p>
          <p className="text-xs text-mono-300 mt-1 tracking-wider">
            V{modelInfo.version} | {modelInfo.feature_count} FEATURES
            {modelInfo.validation_auc && ` | AUC ${modelInfo.validation_auc.toFixed(3)}`}
            {modelInfo.is_trained ? ' | TRAINED' : ' | BASELINE'}
          </p>
        </div>
      )}

      {/* Tier filter cards */}
      <div className="grid grid-cols-5 gap-3">
        {[1, 2, 3, 4, 5].map((tier) => {
          const count = summary?.tier_distribution?.[tier] || 0;
          const total = summary?.scored_sites || 1;
          const percentage = (count / total) * 100;

          return (
            <button
              key={tier}
              onClick={() => setSelectedTier(selectedTier === tier ? undefined : tier)}
              className={`border p-3 text-left transition-colors ${
                selectedTier === tier
                  ? 'border-mono-100 bg-mono-900'
                  : 'border-mono-800 hover:border-mono-600'
              }`}
            >
              <ThreatTierBadge tier={tier as ThreatTier} size="sm" showLabel={false} />
              <p className="text-xl font-bold text-mono-100 mt-2">{count}</p>
              <p className="text-[10px] text-mono-500 tracking-wider">{percentage.toFixed(0)}%</p>
            </button>
          );
        })}
      </div>

      {/* Rankings table */}
      <div className="border border-mono-800">
        <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
          <h3 className="text-xs text-mono-300 tracking-widest">
            RISK RANKINGS {selectedTier && `- TIER ${selectedTier}`}
          </h3>
          {selectedTier && (
            <button
              onClick={() => setSelectedTier(undefined)}
              className="text-[10px] text-mono-500 tracking-widest hover:text-mono-300"
            >
              CLEAR FILTER
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-mono-800 bg-mono-900">
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">#</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">SITE</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">TIER</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">SCORE</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">PHYSICAL</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">REGULATORY</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">FINANCIAL</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">LIABILITY</th>
                <th className="px-4 py-3 text-left text-[10px] text-mono-500 tracking-widest font-medium">SCORED</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-mono-800">
              {sitesLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-mono-500 text-xs tracking-wider">
                    LOADING...
                  </td>
                </tr>
              ) : rankedSites?.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-mono-500 text-xs tracking-wider">
                    NO SCORED SITES
                  </td>
                </tr>
              ) : (
                rankedSites?.map((site, index) => (
                  <tr key={site.id} className="hover:bg-mono-900/50">
                    <td className="px-4 py-3 text-xs text-mono-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <a href={`/sites/${site.id}`} className="text-sm text-mono-200 hover:text-mono-100">
                        {site.name}
                      </a>
                      <p className="text-[10px] text-mono-500 tracking-wider">
                        {site.city}, {site.state}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {site.latest_score && (
                        <ThreatTierBadge tier={site.latest_score.threat_tier} size="sm" showLabel={false} />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-1 bg-mono-800">
                          <div
                            className="h-1 bg-mono-300"
                            style={{ width: `${(site.latest_score?.threat_score || 0) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-mono-400">
                          {formatPercent(site.latest_score?.threat_score)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-mono-500">
                      {formatPercent(site.latest_score?.physical_risk_score)}
                    </td>
                    <td className="px-4 py-3 text-xs text-mono-500">
                      {formatPercent(site.latest_score?.regulatory_risk_score)}
                    </td>
                    <td className="px-4 py-3 text-xs text-mono-500">
                      {formatPercent(site.latest_score?.financial_risk_score)}
                    </td>
                    <td className="px-4 py-3 text-xs text-mono-400">
                      {formatCurrency(site.latest_score?.estimated_cost_to_closure)}
                    </td>
                    <td className="px-4 py-3 text-xs text-mono-500">
                      {formatDate(site.latest_score?.scored_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
